import { useDeferredValue, useMemo, useState } from "react";
import OrganizationRepository from "../repositories/OrganizationRepository";
import { removeLeadingZeros } from "../utils/helpers";

export const SEARCH_TYPES = {
    ORGANIZATION_NAME: "organizationName",
    PARTY_POLICY: "party/policy",
};

export const sortByOrganizationName = (organizations) =>
    [...organizations].sort((a, b) =>
        a.ORGNIZATIONNAME.localeCompare(b.ORGNIZATIONNAME)
    );

// Shared by all Organization Search implementations - filters by ORGNIZATIONNAME prefix match.
export const filterOrganizationsByName = (organizations, value) => {
    const search = value.trim().toLowerCase();

    if (search === "") {
        return [];
    }

    return sortByOrganizationName(
        organizations.filter((organization) =>
            organization.ORGNIZATIONNAME?.toLowerCase().startsWith(search)
        )
    );
};

// matchField defaults to ORGANIZATIONID (what OrganizationRepository.getPolicies stores per policy);
// callers may pass a different field to preserve their existing matching behavior.
export const filterOrganizationsByPolicy = (
    organizations,
    policies,
    value,
    matchField = "ORGANIZATIONID"
) => {
    const matchingPolicies = policies.filter(
        (p) => p.policyId === removeLeadingZeros(value)
    );

    const matches = matchingPolicies
        .map((policy) =>
            organizations.find((o) => o[matchField] === policy.organizationId)
        )
        .filter(Boolean);

    return sortByOrganizationName(matches);
};

// Shared by OrganizationSearchBaseComponent/OrganizationSearchPreferenceComponent -
// CSS class for the related-organization MultiSelect based on selection count.
export const getRelatedOrganizationsClassName = (
    selectedRelatedOrganizations,
    relatedOrganizations
) => {
    const selectedCount = selectedRelatedOrganizations.length;
    const totalCount = relatedOrganizations.length;

    if (selectedCount >= 1 && selectedCount !== totalCount) {
        return selectedCount < 7 ? "test" : "modify";
    }

    return ",";
};

// Shared by OrganizationSearchBaseComponent/OrganizationSearchPreferenceComponent -
// true when the "related organizations" concept collapses to a single org (no multi-select needed).
export const isOrganizationCountOne = (
    allOrganizations,
    organizationPreference,
    organizationsCount,
    relatedOrganizationsLength
) => {
    const preferenceStoredOrg =
        OrganizationRepository.getRelatedOrganizationsFromIds(
            allOrganizations,
            organizationPreference
        );

    if (
        (preferenceStoredOrg &&
            preferenceStoredOrg.length === 1 &&
            preferenceStoredOrg[0].ORGANIZATIONGROUPID === null) ||
        !organizationsCount ||
        relatedOrganizationsLength === 1
    ) {
        return true;
    }
};

// Shared by OrganizationSearchBaseComponent/OrganizationSearchPreferenceComponent -
// builds the sorted related-organization MultiSelect options and whichever are currently selected.
export const buildRelatedOrganizationOptions = (
    relatedOrganizations,
    selectedOrganizationId
) => {
    const options = [];
    const selected = [];

    relatedOrganizations?.forEach((item) => {
        const option = {
            value: item.ORGANIZATIONIDISF,
            label: item.ORGNIZATIONNAME,
            organizationIDISF: item.ORGANIZATIONIDISF,
            organizationGroupId: item.ORGANIZATIONGROUPID,
        };

        options.push(option);
        if (selectedOrganizationId === item.ORGANIZATIONIDISF) {
            selected.push(option);
        }
    });

    const sortedOptions = [...options].sort((a, b) =>
        a.label.localeCompare(b.label)
    );

    return { options: sortedOptions, selected };
};

// Different Organization Search contexts build their related-org option "value" from
// different id fields (e.g. ORGANIZATIONIDISF for Reports/Dashboard vs ORGANIZATIONID for
// Employee Search, which needs the numeric id for its IDHP API calls) even though they all
// share the same viewReportSlice.selectedRelatedOrganizationsForReports selection. Since every
// option always carries a stable organizationIDISF regardless of which context wrote it, use
// that to re-resolve a shared selection into the caller's own locally-shaped options.
export const remapToLocalOptions = (sharedSelection, localOptions) => {
    if (!Array.isArray(sharedSelection) || !Array.isArray(localOptions)) {
        return sharedSelection;
    }

    return sharedSelection
        .map(
            (item) =>
                localOptions.find(
                    (option) =>
                        option.organizationIDISF === item.organizationIDISF
                ) ?? item
        )
        .filter(Boolean);
};

// remapToLocalOptions always returns a fresh array, so dispatching/storing it unconditionally
// hands every subscriber a new reference and retriggers effects that watch it. Compare by the
// stable ISF id so callers can skip no-op updates and avoid an update loop.
export const isSameOptionSelection = (a, b) => {
    if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) {
        return false;
    }

    return a.every(
        (item, index) =>
            item?.organizationIDISF === b[index]?.organizationIDISF &&
            item?.value === b[index]?.value
    );
};

/**
 * Centralizes the organization search/filter/related-org/identifier logic shared by
 * OrganizationSearchBaseComponent, OrganizationSearchPreferenceComponent and
 * views/searchview/OrganizationSearch.js. Not yet wired into those components -
 * additive only, to be adopted incrementally per the consolidation rollout plan.
 */
export default function useOrganizationSearch({
    organizations = [],
    policies = [],
} = {}) {
    const [searchType, setSearchType] = useState(
        SEARCH_TYPES.ORGANIZATION_NAME
    );
    const [searchValue, setSearchValue] = useState("");
    const deferredSearchValue = useDeferredValue(searchValue);

    const results = useMemo(() => {
        if (searchType === SEARCH_TYPES.PARTY_POLICY) {
            return filterOrganizationsByPolicy(
                organizations,
                policies,
                deferredSearchValue
            );
        }

        return filterOrganizationsByName(organizations, deferredSearchValue);
    }, [organizations, policies, searchType, deferredSearchValue]);

    const getRelatedOrganizations = (allOrganizations, organization) =>
        OrganizationRepository.getRelatedOrganizations(
            allOrganizations,
            organization
        );

    const getOrganizationIdentifier = (organization) =>
        OrganizationRepository.getStandardId(organization);

    return {
        searchType,
        setSearchType,
        searchValue,
        setSearchValue,
        results,
        getRelatedOrganizations,
        getOrganizationIdentifier,
    };
}
